/**
 * Validation des formulaires d'inscription (prénom, nom, email) : pas de faux contacts.
 * Fonctions pures, utilisées par klapi-deblocage.js avant tout envoi à Shopify.
 *
 * - email : format strict, domaines jetables (liste CC0 disposable-email-domains, assets/klapi-emails-jetables.json),
 *   emails de test, emails "clavier", faute de frappe sur les grands domaines (suggestion, sans bloquer) ;
 *   jetable et test ne débloquent jamais rien.
 * - prénom / nom : au moins 2 lettres, lettres (accents), espaces, tirets et apostrophes ; pas de valeurs de test.
 */
(function (racine) {
  const MESSAGES = {
    format: 'Il manque un petit quelque chose à cet email.',
    test: 'Bien essayé, mais on vous a eu : « test@ » ne passe pas. Mettez votre vrai email, on ne spamme pas.',
    jetable: "Malheureusement, on vous a eu : cet email s'autodétruit dans 10 minutes. Le verdict, lui, mérite de rester.",
    nom: (valeur) => `« ${valeur} » ? Malheureusement, on vous a eu. Votre vrai prénom suffira.`,
    nomVide: 'Votre prénom et votre nom, s\'il vous plaît : 2 lettres minimum.',
  };

  const LOCAUX_TEST = ['test', 'testing', 'demo', 'fake', 'toto', 'aaa', 'abc', 'asdf', 'azerty', 'qwerty', 'xxx', 'noreply'];
  const DOMAINES_TEST = ['example.com', 'test.com', 'fake.com', 'domain.com', 'email.com', 'mail.com'];
  const NOMS_TEST = ['test', 'toto', 'titi', 'tata', 'aaa', 'abc', 'asdf', 'azerty', 'qwerty', 'xxx', 'fake', 'nom', 'prénom', 'prenom', 'john doe', 'jean dupont'];

  // Grands domaines grand public : base des suggestions de faute de frappe
  const DOMAINES_COURANTS = [
    'gmail.com', 'hotmail.com', 'hotmail.fr', 'outlook.com', 'outlook.fr', 'live.fr', 'live.com', 'msn.com',
    'yahoo.com', 'yahoo.fr', 'icloud.com', 'me.com', 'orange.fr', 'wanadoo.fr', 'free.fr', 'sfr.fr',
    'laposte.net', 'bbox.fr', 'neuf.fr', 'protonmail.com', 'proton.me', 'gmx.fr',
  ];
  // Fautes fréquentes qui ne sont pas à distance 1 ou 2 d'un domaine existant
  const CORRECTIONS = { 'gmail.fr': 'gmail.com', 'gmail.co': 'gmail.com', 'gmail.con': 'gmail.com', 'hotmail.co': 'hotmail.com' };

  function distance(a, b) {
    const d = Array.from({ length: a.length + 1 }, (_, i) => [i]);
    for (let j = 1; j <= b.length; j++) d[0][j] = j;
    for (let i = 1; i <= a.length; i++) {
      for (let j = 1; j <= b.length; j++) {
        d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
        // Inversion de deux lettres voisines ("gmial") : une seule faute
        if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) d[i][j] = Math.min(d[i][j], d[i - 2][j - 2] + 1);
      }
    }
    return d[a.length][b.length];
  }

  /** Domaine corrigé proposé, ou null. maxEcart : 1 pour un domaine jetable connu, 2 sinon. */
  function suggestion(domaine, maxEcart = 2) {
    if (DOMAINES_COURANTS.includes(domaine)) return null;
    if (CORRECTIONS[domaine]) return CORRECTIONS[domaine];
    let meilleur = null;
    let ecart = 3;
    for (const candidat of DOMAINES_COURANTS) {
      const d = distance(domaine, candidat);
      if (d < ecart) {
        ecart = d;
        meilleur = candidat;
      }
    }
    return ecart <= maxEcart ? meilleur : null;
  }

  /**
   * @returns {{ erreur?: 'format'|'test'|'jetable', message?: string, suggestion?: string }}
   */
  function verifierEmail(brut, jetables) {
    const email = String(brut || '').trim().toLowerCase();
    // Format strict : une seule @, pas d'espace, un domaine avec un point
    if (!/^[^\s@]+@[^\s@]+\.[a-z]{2,}$/.test(email) || email.split('@').length !== 2) {
      return { erreur: 'format', message: MESSAGES.format };
    }
    const [local, domaine] = email.split('@');
    if (/\.\./.test(domaine) || domaine.startsWith('.') || domaine.startsWith('-')) {
      return { erreur: 'format', message: MESSAGES.format };
    }
    const base = local.split('+')[0];
    if (LOCAUX_TEST.some((mot) => base === mot || base.startsWith(mot)) || DOMAINES_TEST.includes(domaine)) {
      return { erreur: 'test', message: MESSAGES.test };
    }
    if (/(.)\1{3,}/.test(base) || base.length < 2) {
      return { erreur: 'test', message: MESSAGES.test };
    }
    // Faute de frappe d'abord : certains domaines mal tapés (gmial.com…) sont aussi dans la liste des jetables
    const jetable = jetables && (jetables.has(domaine) || jetables.has(domaine.split('.').slice(-2).join('.')));
    const correction = suggestion(domaine, jetable ? 1 : 2);
    if (correction) return { suggestion: `${local}@${correction}` };
    if (jetable) {
      return { erreur: 'jetable', message: MESSAGES.jetable };
    }
    return {};
  }

  /** @returns {{ erreur?: 'nom', message?: string }} */
  function verifierNom(brut) {
    const valeur = String(brut || '').trim().replace(/\s+/g, ' ');
    if (!/^[\p{L}][\p{L}\s'’-]*[\p{L}]$/u.test(valeur) || valeur.replace(/[\s'’-]/g, '').length < 2) {
      return { erreur: 'nom', message: valeur ? MESSAGES.nom(valeur) : MESSAGES.nomVide };
    }
    const bas = valeur.toLowerCase();
    // 3 lettres identiques à la suite : "aaa", "Jjj" (pas un vrai prénom comme "Annne" mal tapé de 5 lettres)
    if (NOMS_TEST.includes(bas) || /^(\p{L})\1{2,}$/u.test(bas) || /(\p{L})\1{3,}/u.test(bas)) {
      return { erreur: 'nom', message: MESSAGES.nom(valeur) };
    }
    return {};
  }

  const api = { verifierEmail, verifierNom, suggestion, MESSAGES };
  if (typeof module !== 'undefined') module.exports = api;
  else racine.klapiValidation = api;
})(typeof window !== 'undefined' ? window : globalThis);
